/**
 * AuthContext — single source of truth for the logged-in user.
 *
 * Replaces the pattern of `JSON.parse(localStorage.getItem("user"))` scattered
 * across the codebase. Components consume `useAuth()` instead, getting:
 *   - user info
 *   - permission checks
 *   - logout function
 *
 * Existing code that still reads from localStorage will continue to work; we'll
 * migrate it page-by-page as we touch each one.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export interface AuthUser {
  id: number;
  email: string;
  name?: string;
  companyid?: number;
  is_superuser?: boolean;
  is_company?: boolean;
  [key: string]: unknown;
}

interface AuthContextValue {
  user: AuthUser | null;
  permissions: string[];
  enabledFeatures: string[];
  token: string | null;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  hasFeature: (featureKey: string) => boolean;
  refresh: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readUserFromStorage(): AuthUser | null {
  try {
    const raw = localStorage.getItem("user");
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function readPermissionsFromStorage(): string[] {
  try {
    const raw = localStorage.getItem("permissions");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readEnabledFeaturesFromStorage(): string[] {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const features = parsed?.enabled_features;
    return Array.isArray(features) ? features : [];
  } catch {
    return [];
  }
}

function readTokenFromStorage(): string | null {
  return localStorage.getItem("token");
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(readUserFromStorage);
  const [permissions, setPermissions] = useState<string[]>(
    readPermissionsFromStorage,
  );
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>(
    readEnabledFeaturesFromStorage,
  );
  const [token, setToken] = useState<string | null>(readTokenFromStorage);

  const refresh = useCallback(() => {
    setUser(readUserFromStorage());
    setPermissions(readPermissionsFromStorage());
    setEnabledFeatures(readEnabledFeaturesFromStorage());
    setToken(readTokenFromStorage());
  }, []);

  const hasPermission = useCallback(
    (permission: string) => permissions.includes(permission),
    [permissions],
  );

  const hasFeature = useCallback(
    (featureKey: string) => enabledFeatures.includes(featureKey),
    [enabledFeatures],
  );

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("is_logged");
    localStorage.removeItem("userData");
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    setUser(null);
    setPermissions([]);
    setToken(null);
    window.location.href = "/login";
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      permissions,
      enabledFeatures,
      token,
      isAuthenticated: !!token && !!user,
      hasPermission,
      hasFeature,
      refresh,
      logout,
    }),
    [user, permissions, enabledFeatures, token, hasPermission, hasFeature, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
